import { useEffect, useState, type ReactNode } from "react";

import {
  buildPusherTransport,
  RealtimeProvider,
  type PusherTransport,
} from "@thalamos/frontend";
import {
  RealtimeAuthorisationRequestSchema,
  RESOURCE_RELOAD,
} from "@thalamos/haven-common";

import api, { apiHooks } from "../api/index.js";
import { logErrorToDatadog } from "../tracing.js";
import { useUser } from "./UserContext.js";

/**
 * Connects the application to its live updates, on the settings the server
 * reports.
 *
 * Mounted inside `UserProvider`, because there is nothing to subscribe to until
 * somebody is signed in. Nothing is lost by connecting late: a page that mounts
 * first asks to watch its channels before the connection exists, and those are
 * subscribed as soon as it arrives.
 */
export const HavenRealtimeProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { user } = useUser();
  const [{ data: settings }] = apiHooks.configuration.realtime();

  const [connected, setConnected] = useState<PusherTransport | undefined>(
    undefined,
  );

  // Read out as primitives so the effect below is keyed on the values rather
  // than on the object identity of a response, which changes on every refetch
  // and would reconnect for no reason.
  const signedIn = user !== undefined;
  const clientKey = settings?.enabled === true ? settings.clientKey : undefined;
  const cluster = settings?.enabled === true ? settings.cluster : undefined;

  // Opening a socket is a side effect, so it happens in an effect and not while
  // rendering: a value built during render can be built more than once — React
  // renders twice in development, and may discard a memo — and each extra one
  // would be a connection nothing ever closes. Here the cleanup closes exactly
  // the socket its own run opened.
  useEffect(() => {
    if (!signedIn || clientKey === undefined || cluster === undefined) {
      setConnected(undefined);
      return;
    }

    const transport = buildPusherTransport({
      clientKey,
      cluster,
      // Every channel is private, so each subscription is signed by the server
      // against the reader's reach before Pusher admits it.
      authorise: async ({ socketId, channelName }) => {
        const request = RealtimeAuthorisationRequestSchema.safeParse({
          socketId,
          channelName,
        });

        // Rejecting is how the transport is told the subscription was refused;
        // it then abandons that channel. Nothing is shown to the reader — one
        // who may not see a record simply does not watch it.
        if (!request.success) {
          throw new Error(`Refused a subscription to ${channelName}`);
        }

        const response = await api.configuration.realtimeAuthorisation(
          request.data,
        );

        if (response.status !== 200) {
          throw new Error(`Refused a subscription to ${channelName}`);
        }

        return response.data;
      },
    });
    setConnected(transport);

    return () => {
      transport.disconnect();
      setConnected(undefined);
    };
  }, [signedIn, clientKey, cluster]);

  return (
    <RealtimeProvider
      transport={connected?.transport}
      channelPrefix={
        settings?.enabled === true ? settings.channelPrefix : undefined
      }
      eventName={RESOURCE_RELOAD}
      onListenerError={(error: unknown) => {
        logErrorToDatadog(
          error instanceof Error ? error : new Error(String(error)),
        );
      }}
    >
      {children}
    </RealtimeProvider>
  );
};
